import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// TSOG enums have corresponding 'module' (same name as enum) has these methods
// Unlikely that any enums have keys that match method names below as enum keys are typically uppercase

/**
 * Helper methods for ts Enums, which are really wonky in typescript.
 * There are three types: normal (numeric), string, and (rare) const enums.
 * See https://www.typescriptlang.org/docs/handbook/enums.html
 * for overview of numeric, string, and const enums
 *
 * This handles numeric and string enums only, as const enums have no runtime 'type' info so there is nothing to be done.
 */
export class EnumUtils {
  constructor() {}

  // Method Naming convention:
  //  "Key"   is possible enum key (string)
  //  "EnumKey" exact enum key (strong typed: 'keyof EnumT')
  //  "Value" is possible enum value (string | number|
  //  "EnumValue" exact enum value (strongly typed EnumT[keyof EnumT])

  // Only numeric enums have reliable reverse mappings.

  // Two types of enums (with labels and without), each have different type information
  // Enum Def                                      Runtime enumType                        Runtime enumValue       enumKey
  // enum YESNO {YES, NO}                          {0: "YES", 1: "NO", YES: 0, NO: 1},     YESNO.YES=0             YES
  // enum YEPNOPE {"yep", "nope"}                  {0: "yep", 1: "nope", yep: 0, nope: 1}  YEPNOPE.yep=0           yep
  // enum YESNO_STRING {YES = "yep", NO = "nope"}  {YES: "yep", NO: "nope"}                YESNO_STRING.YES="yep"  yep
  // enum YESNO_MIXED {YES = "yep", NO = "nope", MAYBE = 3}  {YES: "yep", NO: "nope", 3: "MAYBE", MAYBE: "3"}      MAYBE

  // NOT SUPPORTED
  // enum const YESNO_CONST {YES, NO}               NA                                    YESNO_CONST.YES=0       NA
  /**
   * @param enumType The enum 'class'
   * @param possibleEnumVal A possible enum value of the enum class (e.g., 3 for a numeric enum, or "yep" for a string enum)
   * @return {string} The enum key of the possibleEnumVal, obtained by looking up the given enumValue in the enumType data structure,
   * and return undefined otherwise
   */
  static enumKeyByValue(enumType, possibleEnumVal) {
    if (!enumType || possibleEnumVal == null) return undefined;
    if (typeof possibleEnumVal === "number") {
      return enumType[possibleEnumVal]; // returns undefined if enumValue is not valid runtime enum value
    }
    // Must be string enum (YESNO_STRING case above), so as long as it is in the 'value' part of a type entry then return it
    // Note this just validates that the provided enumKey (which is the runtime enumValue as well for string enums)
    // is valid (value exists in the enumType map)
    // return Object.keys(enumType).
    for (const [enumKey, enumValue] of Object.entries(enumType)) {
      if (possibleEnumVal === enumValue) return enumKey; // string enum key
    }
    return undefined;
  }

  /**
   * Use this when you want to know the enum key for a given enum value
   * (e.g., EnumUtils.enumKey(RoleType, RoleType.Admin) === "Admin").
   * The enumVal MUST be a valid enum value for the provided enumType or an error will be thrown.
   */
  static enumKeyByEnumValue(enumType, enumVal) {
    const enumKey = EnumUtils.enumKeyByValue(enumType, enumVal);
    // Defensive check: should never happen unless type system is wrong (casting, 'any')
    if (enumKey === undefined) {
      throw new Error(`Can not determine the enum key for the enum value '${enumVal}'`);
    }
    return enumKey;
  }

  /**
   * @return appropriate runtime enum value (string for string enums, number for normal enums)
   * that corresponding to provided enumKey, and return undefined if no match could be found.
   */
  static enumValueByKey(enumType, possibleenumKey) {
    return enumType && possibleenumKey !== "" ? enumType[possibleenumKey] : undefined;
  }

  /**
   * @return runtime enum value if provided possibleEnumVal is a valid enum value for the provided enumType.
   * If it not a valid runtime value of the given enumType, it returns undefined.
   */
  static enumValueByValue(enumType, possibleEnumVal) {
    return EnumUtils.enumKeyByValue(enumType, possibleEnumVal) !== undefined ? possibleEnumVal : undefined;
  }

  /**
   * Support iteration over enum values by returning an array of enum values that can be used in a for...of loop.
   * It works with either numeric enums (default) or string enums.
   * <p>
   * Example: <pre>    for (let view of EnumUtils.values<SampleAppViews>(SampleAppViews)) { ... } </pre>
   * <p>The template type is optional (defaults to 'any'), but useful as it saves caller from having to cast
   * @param {Object} enumType
   * @return {EnumT[]}
   */
  static values(enumType) {
    // WARNING: Probably doesn't handle 'mixed mode' enums where some values are string and some are numeric,
    // but I also don't know of any 'mixed mode' use cases.

    // Assume it is a string enum until we hit first numeric enum val (then it MUST be a numeric enum)
    let isNumericEnumType = false;
    const numVals = [];
    const strVals = [];
    // Do not know if it is string enum or numeric enum; assume string enum until hit first 'number' value

    // Numeric data structure:  {"LazyClasses": 0, "LazyComponents": 1, "0": "LazyClasses", "1": "LazyComponents"}
    // String data structure:  {"LazyClasses": "lazyClasses", "LazyComponents": "lazyCmponents"}

    for (const key in enumType) {
      if (EnumUtils.TSOG_METHOD_NAMES.indexOf(key) >= 0) continue;

      // if (isNaN(Number(view))) {
      const enumVal = enumType[key];
      isNumericEnumType = isNumericEnumType || typeof enumVal === "number";
      if (isNumericEnumType) {
        // Only half of the numeric enum entries are string:number (the others are reverse lookups of string:string)
        if (typeof enumVal === "number") {
          numVals.push(enumVal);
        }
      } else {
        strVals.push(enumVal);
      }
    }
    return isNumericEnumType ? numVals : strVals;
  }
  static size(enumType) {
    // numeric enums have two entries per value (and can be reverse mapped), and string enums have one
    return Object.keys(enumType).filter(key => !EnumUtils.isNumericString(key) && EnumUtils.TSOG_METHOD_NAMES.indexOf(key) < 0).length;
    // see https://stackoverflow.com/questions/38034673/determine-the-number-of-enum-elements-typescript
  }
  static isNumericString(val) {
    try {
      return !!(val && !isNaN(parseFloat(val)));
    } catch (err) {
      return false;
    }
  }
}
// Could probably simplify and have above TSOG_ENUM_METHOD_KEYS based on a const array, but this is simpler (easier to maintain)
// See https://github.com/microsoft/TypeScript/issues/28046
_defineProperty(EnumUtils, "TSOG_METHOD_NAMES", ["fromJson", "name", "indexOf"]);
//# sourceMappingURL=EnumUtils.js.map
